import { useCallback, useEffect, useRef, useState } from "react";
import { useNotification } from "@context/NotificationContext";
import {
  drybulkCargoOperationsService,
  type CargoOperationDetail,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";
import {
  DRYBULK_CARGO_OPS_LANG_BASE,
  DRYBULK_CARGO_OPS_QUERY_KEYS,
  DRYBULK_CARGO_OPS_TELEMETRY,
  PENDING_APPROVAL_STATUS,
  REVIEW_POLL_INTERVAL_MS,
  REVIEW_POLL_TIMEOUT_MS,
} from "../constants";
import { problemMessage, problemStatus } from "../utils/problemDetail";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.review`;

export type DecisionKind = "approve" | "reject";
export type DecisionProgress = "idle" | "processing" | "timed-out";

export const useOperationReview = (operationId: string) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const queryClient = useQueryClient();
  const detailKey = DRYBULK_CARGO_OPS_QUERY_KEYS.detail(operationId);
  const [progress, setProgress] = useState<DecisionProgress>("idle");
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);
  const reading = useRef(false);

  const stopPolling = useCallback(() => {
    if (timer.current !== null) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);
  useEffect(() => stopPolling, [stopPolling]);

  const readDetail = useCallback(
    () =>
      queryClient.fetchQuery({
        queryKey: detailKey,
        queryFn: () => drybulkCargoOperationsService.getDetail(operationId),
        staleTime: 0,
      }),
    [queryClient, detailKey, operationId],
  );

  const refreshQuietly = useCallback(async () => {
    try {
      await readDetail();
    } catch {
      // The next poll or the page's own refresh reads again; nothing here is worth a second message.
    }
  }, [readDetail]);

  const waitForDecision = useCallback(() => {
    stopPolling();
    setProgress("processing");
    const startedAt = Date.now();
    timer.current = setInterval(() => {
      if (reading.current) {
        return;
      }
      reading.current = true;
      void refreshQuietly().then(() => {
        reading.current = false;
        const status = queryClient.getQueryData<CargoOperationDetail>(detailKey)?.status;
        if (status !== PENDING_APPROVAL_STATUS) {
          stopPolling();
          setProgress("idle");
          void queryClient.invalidateQueries({
            queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll(),
          });
        } else if (Date.now() - startedAt >= REVIEW_POLL_TIMEOUT_MS) {
          stopPolling();
          setProgress("timed-out");
        }
      });
    }, REVIEW_POLL_INTERVAL_MS);
  }, [detailKey, queryClient, refreshQuietly, stopPolling]);

  const refused = useCallback(
    (error: unknown) => {
      if (problemStatus(error) === 409) {
        void refreshQuietly();
      }
      notify({
        type: "error",
        title: t("common.error"),
        description: problemMessage(error) ?? t("common.error"),
      });
    },
    [notify, refreshQuietly, t],
  );

  const claim = useMutation({
    mutationFn: () => drybulkCargoOperationsService.claim(operationId),
    onSuccess: () => refreshQuietly(),
    onError: refused,
  });

  const unclaim = useMutation({
    mutationFn: () => drybulkCargoOperationsService.unclaim(operationId),
    onSuccess: () => refreshQuietly(),
    onError: refused,
  });

  const decide = useMutation({
    mutationFn: ({ kind, text }: { kind: DecisionKind; text: string }) =>
      runTransaction(
        {
          module: DRYBULK_CARGO_OPS_TELEMETRY.module,
          screen: DRYBULK_CARGO_OPS_TELEMETRY.operationScreen,
          action: `${kind}_cargo_operation`,
          entityType: "cargo_operation",
          entityId: operationId,
        },
        (transactionId) =>
          kind === "approve"
            ? drybulkCargoOperationsService.approve(operationId, text, { transactionId })
            : drybulkCargoOperationsService.reject(operationId, text, { transactionId }),
      ),
  });

  /** Resolves true when the decision was accepted (the dialog closes), false when the dialog must stay or has nothing to do. */
  const submitDecision = async (kind: DecisionKind, text: string): Promise<boolean> => {
    try {
      await decide.mutateAsync({ kind, text });
    } catch (error) {
      refused(error);
      return problemStatus(error) !== 400;
    }
    waitForDecision();
    return true;
  };

  return {
    claim: () => claim.mutate(),
    unclaim: () => unclaim.mutate(),
    isClaiming: claim.isPending || unclaim.isPending,
    submitDecision,
    isDeciding: decide.isPending,
    progress,
    messages: { processing: t(`${L}.processing`), stillProcessing: t(`${L}.still-processing`) },
  };
};
